import React, {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getIsFilterSelected, getLimitedFilters} from 'components/Filters/utils'
import {ShowMoreButton} from 'components/ShowMoreButton'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {getFacetFilters} from 'modules/sapiSearch/selectors'
import {FilterUrlParam, FilterUrlParams} from 'modules/search/types'

import {Box} from '@vio/atlas/helpers/Box'
import {Pill} from '@vio/atlas/src/components/designSystem/Pill'

import {FacetPillSkeletonLoader} from './FacetPillSkeletonLoader'

const CustomStyledPill = styled(Pill)`
  max-width: 100%;
`

interface Props
  extends Pick<ReturnType<typeof useFiltersForm>, 'toggleMultiFilter'> {
  facet: keyof FilterUrlParams
  limit?: number
  filters: FilterUrlParam
  appliedFilters: FilterUrlParam
  hasAppliedFilters: boolean
  isPopover?: boolean
}
export const FacetsRenderPills: React.FC<Props> = ({
  facet,
  filters,
  appliedFilters,
  limit = 5,
  toggleMultiFilter,
  hasAppliedFilters,
  isPopover = false
}) => {
  const facetFilters = useSelector(getFacetFilters)
  const filtersList = useMemo(
    () => [...Object.values(facetFilters?.[facet] || {})],
    [facetFilters, facet]
  )

  const [isLimited, setIsLimited] = React.useState(true)

  const visibleFilters = useMemo(() => {
    return getLimitedFilters(
      filtersList,
      isLimited,
      limit,
      appliedFilters,
      hasAppliedFilters
    )
  }, [filtersList, isLimited, limit, appliedFilters, hasAppliedFilters])

  const getIsFilterDisabled = useCallback(
    (facilityId: string) => {
      return (
        !facetFilters?.[facet]?.[facilityId]?.isAvailable &&
        !getIsFilterSelected(appliedFilters, facilityId)
      )
    },
    [appliedFilters, facetFilters, facet]
  )

  if (!visibleFilters.length) return null

  return (
    <Box paddingBottom={isPopover ? 's500' : 's000'}>
      <Box
        display="flex"
        flexWrap="wrap"
        rowGap="s400"
        columnGap="s300"
        marginBottom="s400"
      >
        {visibleFilters.map(facetFilter => {
          if (!facetFilter.label) return <FacetPillSkeletonLoader />
          return (
            <CustomStyledPill
              key={facetFilter.id}
              size="lg"
              isSelected={getIsFilterSelected(filters, facetFilter.id)}
              isDisabled={getIsFilterDisabled(facetFilter.id)}
              onClick={() => toggleMultiFilter('facilities', facetFilter.id)}
            >
              {facetFilter.label}
            </CustomStyledPill>
          )
        })}
      </Box>
      {filtersList.length > limit && (
        <ShowMoreButton
          isLimited={isLimited}
          onToggle={() => setIsLimited(prev => !prev)}
        />
      )}
    </Box>
  )
}
